import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, Lock, Loader2, CheckCircle2, Mail, Activity, Bot, Building2, FileText, Bug, Globe2 } from "lucide-react";

const NAV = ["Overview", "Security", "Privacy", "Compliance", "Availability", "Subprocessors", "AI Governance",
  "Responsible Disclosure", "Incident History", "Download Center", "Contact Security", "System Status"];

const STATUS_TONE = { Passing: "bg-emerald-50 text-emerald-600", Warning: "bg-amber-50 text-amber-600",
  "Issue Detected": "bg-red-50 text-red-600", Building: "bg-slate-100 text-slate-500",
  Monitoring: "bg-cobalt/10 text-cobalt", Readiness: "bg-cobalt/10 text-cobalt", operational: "bg-emerald-50 text-emerald-600" };
const SB = ({ v }) => <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[v] || "bg-slate-100 text-slate-500"}`}>{v}</span>;
const Card = ({ children, className = "" }) => <div className={`rounded-3xl bg-white border border-slate-100 p-5 ${className}`}>{children}</div>;

const DocRequestForm = () => {
  const [f, setF] = useState({ requester_name: "", organization: "", email: "", business_reason: "", requested_document: "security_overview", nda_status: "none" });
  const [sent, setSent] = useState(false);
  const cls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-cobalt/30";
  if (sent) return <p className="mt-3 text-emerald-600 text-xs font-semibold" data-testid="docreq-sent">✓ Request received — access is approved individually after verification.</p>;
  return (
    <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="docreq-form">
      <input placeholder="Your name" value={f.requester_name} onChange={(e) => setF({ ...f, requester_name: e.target.value })} data-testid="docreq-name" className={cls} />
      <input placeholder="Organization / firm" value={f.organization} onChange={(e) => setF({ ...f, organization: e.target.value })} data-testid="docreq-org" className={cls} />
      <input placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="docreq-email" className={cls} />
      <select value={f.requested_document} onChange={(e) => setF({ ...f, requested_document: e.target.value })} data-testid="docreq-doc" className={cls}>
        <option value="security_overview">Security Overview / Whitepaper</option>
        <option value="soc2_readiness_summary">SOC 2 Readiness Summary (NDA)</option>
        <option value="soc_report">SOC Report (NDA)</option>
        <option value="pen_test_summary">Pen Test Summary (NDA)</option>
        <option value="security_questionnaire">Completed Security Questionnaire</option>
        <option value="hipaa_safeguards_summary">HIPAA Safeguards Summary</option>
        <option value="hipaa_documentation">HIPAA Documentation Package</option>
        <option value="vendor_program_summary">Vendor Program Summary</option>
        <option value="compliance_documentation">Compliance Documentation</option>
      </select>
      <input placeholder="Business reason" value={f.business_reason} onChange={(e) => setF({ ...f, business_reason: e.target.value })} data-testid="docreq-reason" className={`${cls} sm:col-span-2`} />
      <button onClick={async () => { try { await api.post("/security-docs/request", f); setSent(true); } catch { toast.error("Complete all fields"); } }}
        data-testid="docreq-submit" className="sm:col-span-2 py-2.5 rounded-full bg-navy text-white text-xs font-bold">Request Documentation</button>
    </div>
  );
};

export default function TrustCenterPage() {
  const [data, setData] = useState(undefined);
  const [nav, setNav] = useState("Overview");
  useEffect(() => {
    api.get("/trust-center/full").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (data === undefined) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;
  if (data === null) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6" data-testid="trust-unpublished">
        <div className="text-center max-w-md">
          <Lock className="w-10 h-10 text-white/30 mx-auto" />
          <h1 className="font-heading text-xl font-bold text-white mt-4">Security &amp; Trust Center</h1>
          <p className="text-white/50 text-sm mt-2">This page has not been published yet.</p>
          <Link to="/" className="inline-block mt-5 px-5 py-2 rounded-full bg-white/10 text-white text-xs font-semibold">← Back to Crash Ready</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-50" data-testid="trust-center-page">
      {!data.published && (
        <div className="bg-amber-500 text-white text-center text-[11px] font-bold py-2 tracking-widest" data-testid="trust-preview-banner">
          SUPER ADMIN PREVIEW · UNPUBLISHED · NOT VISIBLE TO PUBLIC
        </div>
      )}
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex items-center gap-2 text-cobalt text-xs font-bold uppercase tracking-widest"><ShieldCheck className="w-4 h-4" /> Crash Ready Trust Center</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-2">Security, Privacy &amp; Compliance — live and verifiable</h1>
          <p className="text-white/70 text-sm mt-2 max-w-2xl">{data.overview || data.statement}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(data.badges || []).map((b) => <span key={b.key} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold" data-testid={`trust-badge-${b.key}`}>✓ {b.label}</span>)}
            <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold">SOC 2: {data.soc2_status}</span>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-6 pb-4 flex gap-1.5 flex-wrap">
          {NAV.map((n) => (
            <button key={n} onClick={() => setNav(n)} data-testid={`trust-nav-${n.replace(/ /g, "-").toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold ${nav === n ? "bg-cobalt text-white" : "bg-white/10 text-white/60 hover:text-white"}`}>{n}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" data-testid={`trust-section-${nav.replace(/ /g, "-").toLowerCase()}`}>
        {nav === "Overview" && (
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Zero-trust philosophy", "Every request is authenticated, authorized against role, organization and case assignment, and audited. Nothing is trusted by network position."],
              ["Least-privilege access", "Attorneys, nurses, providers, pilots and affiliates see only their assigned cases with minimum-necessary fields; sensitive reveals require reauthentication."],
              ["Encryption", "TLS protects data in transit; managed database and object storage encryption protect data at rest; evidence files carry cryptographic hashes."],
              ["Continuous monitoring", "Automated controls re-test on a 6-hour cycle. Previously passing controls are downgraded automatically if evidence goes stale."],
              ["Audit logging", "Immutable, append-only audit events cover authentication, access, exports, consent changes, legal holds and administrative actions."],
              ["Protecting clients", "Health information, evidence and communications stay scoped to the client's case team, with consent-driven disclosures logged in a ledger."],
              ["Protecting law firms", "Strict firm-level tenant isolation — no cross-firm access to clients, documents or work product."],
              ["Protecting providers & evidence", "Original evidence is never overwritten; derivatives are linked separately with full chain-of-custody records."]].map(([t, d]) => (
              <Card key={t}><div className="flex items-center gap-2 font-heading font-bold text-navy text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {t}</div><p className="text-xs text-slate-500 mt-2">{d}</p></Card>
            ))}
          </div>
        )}

        {nav === "Security" && (
          <>
            <Card className="mb-4">
              <h3 className="font-heading font-bold text-navy text-sm">Security scorecard (never inflated)</h3>
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-7 gap-2 text-center">
                {["implemented", "operating", "evidence_collected", "auditor_reviewed", "unknown", "failed", "warning"].map((k) => (
                  <div key={k} className="rounded-xl bg-slate-50 p-3" data-testid={`trust-score-${k}`}>
                    <div className="font-heading font-extrabold text-navy text-lg">{data.scorecard[k]}</div>
                    <div className="text-[9px] uppercase font-bold text-slate-400">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">{data.scorecard.note}</p>
            </Card>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.security_cards.map((c) => (
                <Card key={c.name}>
                  <div className="flex items-center justify-between"><b className="text-navy text-sm font-heading">{c.name}</b><SB v={c.status} /></div>
                  <div className="text-[10px] text-slate-400 mt-2">Last verified: {c.last_verified ? new Date(c.last_verified).toLocaleDateString() : "—"} · Evidence: {c.evidence_available ? "✓ available" : "pending"}</div>
                  <div className="text-[10px] text-slate-300 mt-1">Maps: SOC2 {c.frameworks.soc2_tsc} · HIPAA {c.frameworks.hipaa} · ISO {c.frameworks.iso27001} · NIST {c.frameworks.nist_csf}</div>
                </Card>
              ))}
            </div>
          </>
        )}

        {nav === "Privacy" && (
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Collection & use", "We collect accident, identity, health, insurance and evidence data solely to coordinate documentation, medical care and legal representation. Optional processing is consent-gated."],
              ["Consumer rights", "Access, correction, deletion, portability, opt-out and appeal via the in-app Privacy Request Center, with jurisdiction-specific deadlines."],
              ["Health information", "Health data uses minimum-necessary access, disclosure ledgers and HIPAA-aligned safeguards. Applicability determinations are made with legal counsel."],
              ["Attorney confidentiality", "Potentially privileged materials and work product are tenant-isolated per firm and excluded from analytics and AI training."],
              ["Retention, deletion & legal holds", "Configurable retention policies; deletion goes through a reviewed disposition queue; legal holds always override deletion."],
              ["AI processing", "AI assists with summarization only, with human review, redaction of unnecessary identifiers and no vendor training on your data."]].map(([t, d]) => (
              <Card key={t}><b className="font-heading text-navy text-sm">{t}</b><p className="text-xs text-slate-500 mt-2">{d}</p></Card>
            ))}
          </div>
        )}

        {nav === "Compliance" && (
          <div className="space-y-3">
            {(data.certifications || []).length > 0 && (
              <Card data-testid="trust-certifications">
                <b className="font-heading text-navy text-sm">Certifications &amp; attestations</b>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {data.certifications.map((c) => <span key={c.cert_id} className="px-3 py-1 rounded-full bg-cobalt/10 text-cobalt text-[11px] font-bold">{c.name}: {c.status}{c.issuer ? ` (${c.issuer})` : ""}</span>)}
                </div>
              </Card>
            )}
            {data.frameworks.map((f) => (
              <Card key={f.framework} className="flex items-center justify-between" >
                <div><b className="font-heading text-navy text-sm">{f.framework}</b>
                  <div className="text-[11px] text-slate-400">{f.controls_mapped} mapped controls · {f.operating} operating with evidence</div></div>
                <SB v={f.public_status} />
              </Card>
            ))}
            <p className="text-[10px] text-slate-400">Statuses progress Planning → Building → Readiness → External Audit Scheduled → Under Independent Audit → Certified. Certification labels appear only after third-party certification.</p>
          </div>
        )}

        {(nav === "Availability" || nav === "System Status") && (
          <Card>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" /> System status</h3>
              <SB v={data.status.overall} />
            </div>
            <div className="text-[11px] text-slate-400 mt-1">Checked {new Date(data.status.checked_at).toLocaleString()} · Uptime (last {data.status.history?.length || 0} checks): {data.status.uptime_pct_60_checks ?? "collecting"}%</div>
            <div className="mt-3 grid sm:grid-cols-3 gap-2">
              {data.status.services.map((s) => (
                <div key={s.name} className="flex justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs" data-testid={`trust-svc-${s.name.replace(/ /g, "-")}`}>
                  <span className="text-slate-600">{s.name}</span><SB v={s.status} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-1 items-end h-10">
              {(data.status.history || []).slice().reverse().map((h, i) => (
                <div key={i} title={h.at} className={`w-2 rounded-t ${h.up ? "bg-emerald-400 h-8" : "bg-red-400 h-4"}`} />
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">No active incident banner. Maintenance windows are announced here in advance.</p>
          </Card>
        )}

        {nav === "Subprocessors" && (
          <div className="space-y-2">
            {data.subprocessors.map((v) => (
              <Card key={v.company} className="flex flex-wrap items-center justify-between gap-2">
                <div><b className="font-heading text-navy text-sm flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-cobalt" /> {v.company}</b>
                  <div className="text-[11px] text-slate-400">{v.purpose} · {v.country} · {v.active ? "Active" : "Planned"}</div></div>
                <div className="flex gap-1.5 text-[10px] font-bold">
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">SOC 2: {v.soc2}</span>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">BAA: {v.baa}</span>
                  <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Risk: {v.risk}</span>
                </div>
              </Card>
            ))}
          </div>
        )}

        {nav === "AI Governance" && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              {["Qualified human review is required for all medical and legal AI outputs", "No AI medical diagnosis or prescriptions",
                "No AI legal advice or final legal decisions", "No attorney selection based on protected characteristics",
                "Prompts and outputs are logged with model and version", "Unnecessary identifiers are redacted before AI processing",
                "Vendor training on Crash Ready data is not authorized", "Firms can disable optional AI features"].map((t) => (
                <Card key={t} className="flex items-start gap-2 text-xs text-slate-600"><Bot className="w-4 h-4 text-cobalt shrink-0 mt-0.5" /> {t}</Card>
              ))}
            </div>
            <Card>
              <b className="font-heading text-navy text-sm">Model providers in use</b>
              {data.ai_systems.map((a) => (
                <div key={a.name} className="mt-2 flex justify-between text-xs rounded-xl bg-slate-50 px-3 py-2">
                  <span className="text-slate-600"><b>{a.name}</b> — {a.purpose}</span>
                  <span className="text-slate-400">{a.provider} · human review {a.human_review_required ? "✓" : "—"}</span>
                </div>
              ))}
            </Card>
          </div>
        )}

        {nav === "Responsible Disclosure" && (
          <Card>
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Bug className="w-4 h-4 text-red-500" /> Vulnerability reporting</h3>
            <div className="mt-3 text-xs text-slate-600 space-y-2">
              <p><b>Scope:</b> {data.disclosure.scope}</p>
              <div><b>Rules:</b><ul className="mt-1 space-y-1">{data.disclosure.rules.map((r, i) => <li key={i}>• {r}</li>)}</ul></div>
              <p><b>Safe harbor:</b> {data.disclosure.safe_harbor}</p>
              <p><b>Timeline:</b> {data.disclosure.timeline}</p>
              <p><b>Report to:</b> {data.disclosure.contact} (PGP key available on request). Researchers are acknowledged with permission after remediation.</p>
            </div>
          </Card>
        )}

        {nav === "Incident History" && (
          <Card>
            <h3 className="font-heading font-bold text-navy text-sm">Incident history</h3>
            {data.incident_history.length ? data.incident_history.map((i, idx) => (
              <div key={idx} className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <b>{i.date}</b> — {i.type} · {i.stage}<div className="text-slate-400">{i.summary}</div>
              </div>
            )) : <p className="text-xs text-slate-400 mt-2" data-testid="trust-no-incidents">No publicly reportable incidents. Internal investigation details are never exposed here.</p>}
          </Card>
        )}

        {nav === "Download Center" && (
          <Card>
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-cobalt" /> Legal &amp; security documentation</h3>
            <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
              {data.documents.map((d) => <div key={d} className="rounded-xl bg-slate-50 px-3 py-2 flex justify-between"><span>{d}</span><span className="text-slate-300">{d.includes("request") || d.includes("NDA") ? "Approval required" : "On request"}</span></div>)}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Documents requiring approval are watermarked, expire automatically, and every view/download is logged.</p>
            <DocRequestForm />
          </Card>
        )}

        {nav === "Contact Security" && (
          <Card>
            <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-cobalt" /> Contact</h3>
            <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs">
              {[["Security", data.disclosure.contact], ["Privacy", data.disclosure.privacy], ["Compliance", data.disclosure.compliance]].map(([t, e]) => (
                <div key={t} className="rounded-xl bg-slate-50 px-3 py-3 text-center"><b className="text-navy">{t}</b><div className="text-cobalt mt-1">{e}</div></div>
              ))}
            </div>
            <DocRequestForm />
          </Card>
        )}
      </main>
      <footer className="max-w-4xl mx-auto px-6 pb-10">
        <p className="text-[11px] text-slate-400 text-center" data-testid="trust-statement">{data.statement}</p>
        <p className="text-[10px] text-slate-300 text-center mt-2">{data.disclaimer}</p>
      </footer>
    </div>
  );
}
